/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import {
	ContInspectionPayload,
	PhyInspContainer,
} from "../platePhysicalInspection.types";
import { postAutoclaveServices } from "../../../../../service/postAutoclaveServices";

interface Props {
	stats: any;
	closeConfirmationModal: () => void;
	containers: PhyInspContainer[];
	refreshData: () => void;
	showToast: (message: string, toastType: "success" | "error") => void;
	mediaBatchId: string;
}

const PIConfirmModal: React.FC<Props> = ({
	stats,
	closeConfirmationModal,
	containers,
	refreshData,
	showToast,
	mediaBatchId
}) => {
	const { t } = useTranslation();
	const handleConfirm = async () => {
		const payload: ContInspectionPayload = {
			media_batch_id: mediaBatchId,
			inspections: containers
				.filter((cont) => cont.rejected !== undefined)
				.map((cont) => ({
					container_id: Number(cont.container_id),
					inspection_reason_id: cont.inspection_reason_id,
					...(cont.rejected === true && {
						rejection_reason: cont.rejection_reason ?? "",
					}),
				})),
		};

		try {
			const response =
				await postAutoclaveServices.postContainerInspections(payload);
			if (response.statusCode !== 200) {
				showToast(
					response.data.message || "An unexpected error occurred",
					"error",
				);
			} else {
				console.log(response);
			}
		} catch (error: any) {
			console.log(error);
			showToast(
				error?.response?.data?.message || "An unexpected error occurred",
				"error",
			);
		} finally {
			closeConfirmationModal();
			refreshData();
		}
	};
	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 animate-in fade-in duration-300">
			<div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-300">
				<div className="p-12 text-center">
					<div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-indigo-100">
						<span className="material-symbols-outlined text-indigo-600 text-5xl">
							fact_check
						</span>
					</div>
					<h3 className="text-2xl font-black text-[#1E293B] mb-4 tracking-tight">
						{t(
							"postAutoclaveProcess.physicalInspection.confirmInspection",
						)}
						?
					</h3>
					<div className="bg-slate-50 rounded-3xl p-8 mb-8 space-y-6">
						<p className="text-sm font-bold text-slate-500 leading-relaxed">
							{t(
								"postAutoclaveProcess.physicalInspection.allPlatesHavebeenInspected",
							)}
						</p>
						<div className="grid grid-cols-2 gap-4">
							<div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
								<p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">
									{t(
										"postAutoclaveProcess.physicalInspection.reusable",
									)}
								</p>
								<p className="text-2xl font-black text-emerald-600">
									{stats.passed}
								</p>
							</div>
							<div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
								<p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">
									{t(
										"postAutoclaveProcess.physicalInspection.rejected",
									)}
								</p>
								<p className="text-2xl font-black text-red-600">
									{stats.rejected}
								</p>
							</div>
						</div>
						<div className="h-px bg-slate-200 w-full"></div>
						<p className="text-sm font-black text-[#1E293B]">
							{t(
								"postAutoclaveProcess.physicalInspection.doYouWantToConfirm",
							)}
						</p>
					</div>

					<div className="flex gap-4">
						<button
							onClick={() => closeConfirmationModal()}
							className="flex-1 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all"
						>
							{t("postAutoclaveProcess.physicalInspection.cancel")}
						</button>
						<button
							onClick={handleConfirm}
							className="flex-1 px-8 py-4 bg-indigo-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all"
						>
							{t(
								"postAutoclaveProcess.physicalInspection.confirm&Complete",
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PIConfirmModal;
